import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import useEmblaCarousel from 'embla-carousel-react';
import type { GoogleCreative, GoogleCreativeFormat, GoogleCreativeVariation } from '../api/types';
import GoogleContentPreview from './GoogleContentPreview';
import GoogleContentPreviewButtons from './GoogleContentPreviewButtons';

type GoogleVariationSlideType = GoogleCreativeFormat | 'image';

interface GoogleVariationSlide {
  key: string;
  type: GoogleVariationSlideType;
  contentUrl?: string;
  previewUrl?: string;
  posterUrl?: string;
  sourceVideoUrl?: string;
  storedVideoUrl?: string;
  sourceImageUrl?: string;
  storedImageUrl?: string;
}

interface GoogleCreativeVariationsCarouselProps {
  creative: GoogleCreative;
  containerId?: string;
  autoHeight?: boolean;
  hoverControls?: boolean;
}

const getNonEmptyUrl = (url?: string): string | undefined => (url?.trim() ? url : undefined);

// the source pair (YouTube video + its thumbnail) wins when available; otherwise fall back to the
// mirrored pair on our storage — never mix a source asset with a mirrored one.
// `previewUrl` stays the mirrored thumbnail: the nav panel copies and downloads it.
const getVariationVideoSources = (variation: GoogleCreativeVariation) => {
  const sourceVideoUrl = getNonEmptyUrl(variation.videoUrl);
  const storedVideoUrl = getNonEmptyUrl(variation.s3VideoUrl);
  const sourceThumbnailUrl = getNonEmptyUrl(variation.thumbnailUrl);
  const storedThumbnailUrl = getNonEmptyUrl(variation.s3ThumbnailUrl);
  const common = { previewUrl: variation.s3ThumbnailUrl, sourceVideoUrl, storedVideoUrl };

  if (sourceVideoUrl) {
    return { ...common, contentUrl: sourceVideoUrl, posterUrl: sourceThumbnailUrl ?? storedThumbnailUrl };
  }

  return { ...common, contentUrl: storedVideoUrl, posterUrl: storedThumbnailUrl ?? sourceThumbnailUrl };
};

const getVariationImageSources = (variation: GoogleCreativeVariation) => {
  const sourceImageUrl = getNonEmptyUrl(variation.imageUrl);
  const storedImageUrl = getNonEmptyUrl(variation.s3ImageUrl);

  return {
    contentUrl: storedImageUrl ?? sourceImageUrl,
    previewUrl: variation.s3ThumbnailUrl,
    sourceImageUrl,
    storedImageUrl,
  };
};

const getVariationSlide = (variation: GoogleCreativeVariation, format: GoogleCreativeFormat, index: number): GoogleVariationSlide => {
  if (format === 'video') {
    const videoSources = getVariationVideoSources(variation);

    if (videoSources.contentUrl) {
      return { key: `variation-video-${index}`, type: 'video', ...videoSources };
    }
  }

  return {
    key: `variation-image-${index}`,
    type: format === 'text' ? 'text' : 'image',
    ...getVariationImageSources(variation),
  };
};

const GoogleCreativeVariationsCarousel: FC<GoogleCreativeVariationsCarouselProps> = ({
  creative,
  containerId,
  autoHeight = false,
  hoverControls = false,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const slides = useMemo(() => {
    const mappedSlides = creative.variations.map((variation, index) => getVariationSlide(variation, creative.format, index));
    const usableSlides = mappedSlides.filter((slide) => !!slide.contentUrl);

    if (creative.format !== 'video') {
      return usableSlides;
    }

    return usableSlides.sort((a, b) => {
      if (a.type === b.type) return 0;
      if (a.type === 'video') return -1;
      return 1;
    });
  }, [creative.variations, creative.format]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, watchDrag: slides.length > 1 });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const handlePrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const handleNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    setIsVideoPlaying(false);
  }, [selectedIndex]);

  if (slides.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: autoHeight ? 200 : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.900',
        }}
      >
        <Typography variant="body2" color="common.white">
          No variation media available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: autoHeight ? 'auto' : '100%', overflow: 'hidden' }}>
      <Box ref={emblaRef} sx={{ overflow: 'hidden', width: '100%', height: autoHeight ? 'auto' : '100%' }}>
        <Box sx={{ display: 'flex', height: autoHeight ? 'auto' : '100%', alignItems: 'stretch' }}>
          {slides.map((slide, index) => (
            <Box
              key={slide.key}
              sx={{ flex: '0 0 100%', minWidth: 0, position: 'relative', height: autoHeight ? 'auto' : '100%' }}
            >
              <GoogleContentPreview
                creativeId={creative.adCreativeId}
                adLibraryUrl={creative.adLibraryUrl}
                contentUrl={slide.contentUrl}
                previewUrl={slide.previewUrl}
                posterUrl={slide.posterUrl}
                mediaType={slide.type}
                containerId={containerId}
                isActive={index === selectedIndex}
                autoHeight={autoHeight}
                hideButtons
                onPlayingChange={index === selectedIndex ? setIsVideoPlaying : undefined}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {!isVideoPlaying && (
        <GoogleContentPreviewButtons
          mediaType={creative.format}
          creativeId={creative.adCreativeId}
          adLibraryUrl={creative.adLibraryUrl}
          contentUrl={slides[selectedIndex]?.contentUrl}
          previewUrl={slides[selectedIndex]?.previewUrl}
          sourceVideoUrl={slides[selectedIndex]?.sourceVideoUrl}
          storedVideoUrl={slides[selectedIndex]?.storedVideoUrl}
          sourceImageUrl={slides[selectedIndex]?.sourceImageUrl}
          storedImageUrl={slides[selectedIndex]?.storedImageUrl}
          containerId={containerId}
        />
      )}

      {slides.length > 1 && (
        <>
          <Tooltip title="Previous variation">
            <Box
              component="span"
              className={hoverControls ? 'creative-nav-panel' : undefined}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                pl: 1,
                zIndex: 5,
                pointerEvents: 'none',
              }}
            >
              <IconButton
                size="small"
                disabled={!canScrollPrev}
                onClick={handlePrev}
                sx={{
                  pointerEvents: 'auto',
                  bgcolor: 'rgba(0, 0, 0, 0.55)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(0,0,0,0.3)' },
                }}
              >
                <KeyboardArrowLeft />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title="Next variation">
            <Box
              component="span"
              className={hoverControls ? 'creative-nav-panel' : undefined}
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                pr: 1,
                zIndex: 5,
                pointerEvents: 'none',
              }}
            >
              <IconButton
                size="small"
                disabled={!canScrollNext}
                onClick={handleNext}
                sx={{
                  pointerEvents: 'auto',
                  bgcolor: 'rgba(0, 0, 0, 0.55)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(0,0,0,0.3)' },
                }}
              >
                <KeyboardArrowRight />
              </IconButton>
            </Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default GoogleCreativeVariationsCarousel;
